import CancelIcon from '@eduzz/houston-icons/Cancel';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, FormControlLabel, Checkbox, IconButton } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import CoProducerClass from 'classes/CoProducer';
import CustomMaskedInput from 'components/MaskedInput';
import * as React from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { finalize } from 'rxjs/operators';
import { sendEventToGA } from 'v2/helpers/analytics';
import productService from 'v2/services/product';

import { useStyles } from './styles';

type SolidaritySquadModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  productId: number;
};

const PROJECT_ELEVAR_ID = 75879698;
const DEFAULT_COMMISSION_VALUE = 5;
const GA_CATEGORY = 'SS Modal - Projeto Elevar';

enum CloseReason {
  NOT_NOW_BUTTON,
  CLOSE_BUTTON,
}

function SolidaritySquadModal({ isOpen, handleClose: handleCloseProp, productId }: SolidaritySquadModalProps) {
  const classes = useStyles();
  const [updateCoproducers] = useCallbackObservable((productId, coproducers) => {
    setIsAdding(true);
    return productService.updateCoproducers(productId, coproducers).pipe(
      finalize(() => {
        setIsAdding(false);
        toast.success('Doação automática configurada com sucesso');
        handleClose();
      })
    );
  }, []);

  const [commissionValue, setCommissionValue] = React.useState(DEFAULT_COMMISSION_VALUE);
  const [commissionError, setCommissionError] = React.useState('');
  const [isTermsAccepted, setIsTermsAccepted] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);

  function validateCommissionValue(value: number) {
    const minimumCommissionValue = 0.01;
    if (value < minimumCommissionValue) {
      setCommissionError('O valor de porcentagem tem que ser no mínimo 0,01');
    } else {
      setCommissionError(null);
    }
  }

  function handleCommissionValueChange(value: string) {
    const formattedValue = Number(value.replace(',', '.'));
    validateCommissionValue(formattedValue);
    setCommissionValue(formattedValue);
  }

  function handleTermsChange(e: React.ChangeEvent<HTMLInputElement>) {
    setIsTermsAccepted(e.target.checked);
  }

  function createElevarAsCoProducer() {
    return new CoProducerClass({
      new: true,
      pending: false,
      business_name: 'Instituto Elevar',
      email: 'contato@projetoelevar.com.br',
      comission_type: 'P',
      comission_value: commissionValue,
      customer_id: PROJECT_ELEVAR_ID,
    });
  }

  function removeCommissionFromProducer(coproducers: any[]) {
    return coproducers.map((coproducer) => {
      if (coproducer.is_producer) {
        coproducer.comission_value -= commissionValue;
      }
      return coproducer;
    });
  }

  function handleSubmit(productId: number) {
    productService.getCoproducers(productId).subscribe((coproducers) => {
      const updatedCoproducers = removeCommissionFromProducer(coproducers.active);
      updateCoproducers(productId, [...updatedCoproducers, createElevarAsCoProducer()]);
    });
    sendEventToGA({
      category: GA_CATEGORY,
      action: 'Save Donation',
    });
  }

  function handleClose(closeReason?: CloseReason) {
    handleCloseProp();
    if (closeReason === CloseReason.CLOSE_BUTTON) {
      sendEventToGA({
        category: GA_CATEGORY,
        action: 'Close',
      });
    }
    if (closeReason === CloseReason.NOT_NOW_BUTTON) {
      sendEventToGA({
        category: GA_CATEGORY,
        action: 'Not Now',
      });
    }
  }

  return (
    <Dialog classes={{ paper: classes.paper }} open={isOpen} onClose={handleClose} maxWidth='md' scroll='body'>
      <div className={classes.imageWrapper}>
        <img className={classes.image} src='/img/modal-elevar.png' alt='Projeto elevar' />
      </div>
      <DialogTitle className={classes.title}>
        Faça a diferença e transforme vidas!
        <div>
          <IconButton size='small' aria-label='Fechar modal' onClick={() => handleClose(CloseReason.CLOSE_BUTTON)}>
            <CancelIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <p className={classes.subtitle}>Junte-se ao Instituto Elevar, uma organização de Assistência Social</p>
            <p className={classes.contentText}>
              Transformamos vidas por meio da educação, tecnologia, esporte, arte, cultura, qualificação profissional e empreendedorismo.
            </p>
          </Grid>
          <Grid item xs={12}>
            <CustomMaskedInput
              id='comission_value'
              className={classes.input}
              name='comission_value'
              mask='percent'
              label='Porcentagem da contribuição'
              helperText={commissionError}
              error={!!commissionError}
              startAdornment='%'
              fullWidth
              disableUnderline
              onChange={handleCommissionValueChange}
              value={String(commissionValue)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                min: 0.01,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={isTermsAccepted} onChange={handleTermsChange} />}
              label={`Concordo em contribuir com ${commissionValue}% sobre cada venda deste produto para o Instituto Elevar.`}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant='outlined' onClick={() => handleClose(CloseReason.NOT_NOW_BUTTON)}>
          Não quero contribuir
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!!commissionError || !isTermsAccepted || isAdding}
          startIcon={isAdding && <CircularProgress size={20} />}
          onClick={() => handleSubmit(productId)}
        >
          Fazer a diferença
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { SolidaritySquadModal };
